import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ArrowLeft, RefreshCw, Search, Package, TrendingDown, Factory, Users, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StoreLowStock = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Edit/Delete state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editForm, setEditForm] = useState({
    product_code: "",
    product_type: "",
    min_stock: "",
    mill_name: "",
    dealer_name: ""
  });
  
  // Wooden Pallet available stock
  const [woodenPalletStock, setWoodenPalletStock] = useState([]);

  useEffect(() => {
    fetchLowStock();
    fetchWoodenPalletStock();
  }, []);

  const fetchLowStock = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/materials/low-stock`);
      setLowStockItems(response.data.items || []);
    } catch (error) {
      toast.error("Failed to fetch low stock data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWoodenPalletStock = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/inventory`);
      // Filter for Wooden Pallet items that are available
      const woodenPallets = response.data.filter(item => 
        item.product === "Wooden Pallet" && item.status === "available"
      );
      setWoodenPalletStock(woodenPallets);
    } catch (error) {
      console.error("Error fetching wooden pallet stock:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLowStock();
    await fetchWoodenPalletStock();
    setRefreshing(false);
    toast.success("Low stock data refreshed");
  };

  // Open Edit Dialog
  const openEditDialog = (item) => {
    setSelectedItem(item);
    setEditForm({
      product_code: item.product_code || "",
      product_type: item.product_type || "",
      min_stock: item.min_stock?.toString() || "",
      mill_name: item.mill_name || "",
      dealer_name: item.dealer_name || ""
    });
    setIsEditDialogOpen(true);
  };

  // Handle Edit Material
  const handleEditMaterial = async () => {
    if (!selectedItem?.id) {
      toast.error("No item selected");
      return;
    }
    
    try {
      await axios.put(`${BACKEND_URL}/api/materials/${selectedItem.id}`, {
        ...editForm,
        min_stock: parseFloat(editForm.min_stock) || 0
      });
      toast.success("Material updated successfully");
      setIsEditDialogOpen(false);
      setSelectedItem(null);
      fetchLowStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update material");
    }
  };

  // Open Delete Dialog
  const openDeleteDialog = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  // Handle Delete Material
  const handleDeleteMaterial = async () => {
    if (!selectedItem?.id) {
      toast.error("No item selected");
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/materials/${selectedItem.id}`);
      toast.success("Material deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchLowStock();
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  // Filter by search
  const filteredItems = lowStockItems.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.product_code?.toLowerCase().includes(term) ||
      item.product_type?.toLowerCase().includes(term) ||
      item.mill_name?.toLowerCase().includes(term) ||
      item.dealer_name?.toLowerCase().includes(term)
    );
  });

  // Calculate stats
  const totalDeficit = lowStockItems.reduce((sum, item) => sum + (item.min_stock - item.current_stock), 0);
  const criticalItems = lowStockItems.filter(item => item.current_stock === 0).length;
  const uniqueMills = [...new Set(lowStockItems.map(i => i.mill_name).filter(Boolean))].length;
  const uniqueDealers = [...new Set(lowStockItems.map(i => i.dealer_name).filter(Boolean))].length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/store/dashboard")}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-red-400 tracking-wider flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 animate-pulse" />
              Low Stock Report
            </h1>
            <p className="text-gray-400 mt-1">Items requiring reorder</p>
          </div>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
          disabled={refreshing}
          data-testid="refresh-low-stock-btn"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-900/40 to-red-800/20 border-2 border-red-500/50">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <AlertTriangle className="h-10 w-10 text-red-500" />
              <p className="text-4xl font-black text-red-500">{loading ? "..." : lowStockItems.length}</p>
              <p className="text-sm font-medium text-red-300/70">Items Below Min Stock</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 border-2 border-orange-500/30">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <TrendingDown className="h-10 w-10 text-orange-400" />
              <p className="text-4xl font-black text-orange-400">{loading ? "..." : totalDeficit}</p>
              <p className="text-sm font-medium text-orange-300/70">Total Deficit Units</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/20 border-2 border-gray-600/30">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <Package className="h-10 w-10 text-cyan-400" />
              <p className="text-4xl font-black text-cyan-400">{loading ? "..." : criticalItems}</p>
              <p className="text-sm font-medium text-cyan-300/70">Out of Stock (0 qty)</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-2 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex flex-col items-center text-center gap-2">
              <Factory className="h-10 w-10 text-purple-400" />
              <p className="text-4xl font-black text-purple-400">{loading ? "..." : uniqueMills}</p>
              <p className="text-sm font-medium text-purple-300/70">Mills Affected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-black/60 border-2 border-red-500/30">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by Product Code, Type, Mill, or Dealer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-black/50 border-red-500/30 text-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* Low Stock Table */}
      <Card className="bg-black/60 border-2 border-red-500/50">
        <CardHeader>
          <CardTitle className="text-red-400 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Low Stock Items ({filteredItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading low stock data...</div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 mx-auto mb-4 text-cyan-400" />
              <p className="text-xl font-bold text-cyan-400 mb-2">All Stock Levels OK!</p>
              <p className="text-gray-400">No items are below their minimum stock level.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-red-500/30 hover:bg-transparent">
                    <TableHead className="text-red-300">Product Code (RM Name)</TableHead>
                    <TableHead className="text-red-300">Product Type</TableHead>
                    <TableHead className="text-red-300 text-center">Min Stock</TableHead>
                    <TableHead className="text-red-300 text-center">Current Stock</TableHead>
                    <TableHead className="text-red-300 text-center">Deficit</TableHead>
                    <TableHead className="text-red-300">Mill Name</TableHead>
                    <TableHead className="text-red-300">Dealer Name</TableHead>
                    <TableHead className="text-red-300 text-center">Status</TableHead>
                    <TableHead className="text-red-300 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item, idx) => {
                    const deficit = item.min_stock - item.current_stock;
                    const isOutOfStock = item.current_stock === 0;
                    const isCritical = item.current_stock <= item.min_stock * 0.3;
                    
                    return (
                      <TableRow
                        key={idx}
                        className={cn(
                          "border-red-500/20",
                          isOutOfStock ? "bg-red-500/20 hover:bg-red-500/30" : "hover:bg-red-500/10"
                        )}
                      >
                        <TableCell className="font-mono font-bold text-white">
                          {item.product_code}
                        </TableCell>
                        <TableCell className="text-gray-300">
                          {item.product_type || "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full font-bold">
                            {item.min_stock}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            "px-3 py-1 rounded-full font-bold",
                            isOutOfStock 
                              ? "bg-red-500/30 text-red-400" 
                              : isCritical 
                                ? "bg-orange-500/30 text-orange-400"
                                : "bg-cyan-600/30 text-cyan-400"
                          )}>
                            {item.current_stock}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full font-bold">
                            -{deficit}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-300">
                          {item.mill_name || "-"}
                        </TableCell>
                        <TableCell className="text-gray-300">
                          {item.dealer_name || "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {isOutOfStock ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-500/30 text-red-400 border border-red-500/50 animate-pulse">
                              OUT OF STOCK
                            </span>
                          ) : isCritical ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-orange-500/30 text-orange-400 border border-orange-500/50">
                              CRITICAL
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-cyan-600/30 text-cyan-400 border border-gray-600/50">
                              LOW
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(item)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(item)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wooden Pallet Available Stock Table */}
      {woodenPalletStock.length > 0 && (
        <Card className="bg-black/60 border-2 border-cyan-500/30">
          <CardHeader>
            <CardTitle className="text-cyan-400 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Available Stock - Wooden Pallet
              <span className="ml-2 px-3 py-1 bg-emerald-500/20 text-cyan-300 rounded-full text-sm">
                {woodenPalletStock.length} items | Total Qty: {woodenPalletStock.reduce((sum, item) => sum + (item.qty || 1), 0)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-cyan-500/30 hover:bg-transparent">
                    <TableHead className="text-cyan-300">Reel ID</TableHead>
                    <TableHead className="text-cyan-300">RM Name</TableHead>
                    <TableHead className="text-cyan-300">In Date</TableHead>
                    <TableHead className="text-cyan-300 text-center">Qty</TableHead>
                    <TableHead className="text-cyan-300">Mill - Dealer</TableHead>
                    <TableHead className="text-cyan-300 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {woodenPalletStock.map((item, idx) => (
                    <TableRow key={idx} className="border-cyan-500/20 hover:bg-emerald-500/10">
                      <TableCell className="font-mono font-bold text-white">
                        {item.reel_id}
                      </TableCell>
                      <TableCell className="text-cyan-300 font-medium">
                        {item.rm_name || item.item_name || "-"}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.in_date || "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="px-3 py-1 bg-emerald-500/20 text-cyan-400 rounded-full font-bold">
                          {item.qty || 1}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {item.mill_name && item.dealer_name 
                          ? `${item.mill_name} - ${item.dealer_name}` 
                          : item.mill_name || item.dealer_name || "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/30 text-cyan-400 border border-cyan-500/50">
                          AVAILABLE
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Card className="bg-black/60 border-2 border-red-500/30">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/store/inventory")}
              variant="outline"
              className="border-cyan-500/50 text-cyan-400 hover:bg-emerald-500/20"
            >
              <Package className="h-4 w-4 mr-2" />
              View Full Inventory
            </Button>
            <Button
              onClick={() => navigate("/store/purchase-order")}
              className="bg-red-500 hover:bg-red-600 text-white font-bold"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Create Purchase Order
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-gray-900 border-blue-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-blue-400 text-xl">Edit Material</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label className="text-blue-300">Product Code (RM Name)</Label>
              <Input
                value={editForm.product_code}
                onChange={(e) => setEditForm({ ...editForm, product_code: e.target.value })}
                className="bg-black/50 border-blue-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-blue-300">Product Type</Label>
              <Input
                value={editForm.product_type}
                onChange={(e) => setEditForm({ ...editForm, product_type: e.target.value })}
                className="bg-black/50 border-blue-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-blue-300">Min Stock</Label>
              <Input
                type="number"
                value={editForm.min_stock}
                onChange={(e) => setEditForm({ ...editForm, min_stock: e.target.value })}
                className="bg-black/50 border-blue-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-blue-300">Mill Name</Label>
              <Input
                value={editForm.mill_name}
                onChange={(e) => setEditForm({ ...editForm, mill_name: e.target.value })}
                className="bg-black/50 border-blue-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-blue-300">Dealer Name</Label>
              <Input
                value={editForm.dealer_name}
                onChange={(e) => setEditForm({ ...editForm, dealer_name: e.target.value })}
                className="bg-black/50 border-blue-500/30 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleEditMaterial} className="bg-blue-500 hover:bg-blue-600 text-white font-bold">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Material</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to delete <span className="font-bold text-white">{selectedItem?.product_code}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-700 text-white hover:bg-gray-600 border-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMaterial} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StoreLowStock;
